import { useState } from "react";
import { useGlobalContext } from "@/context/GlobalContext";
import { useBrand, findBoardTag } from "@/lib/brand-context";
import { BoardTag, Article, BOARD_CONFIG } from "@/types";
import { ChevronLeft, Eye, Download, Clock, X } from "lucide-react";
import { Link } from "react-router-dom";
import { ArticleMarkdown, cleanArticleContent } from "@/lib/articleContent";

function PreviewModal({
  article,
  onClose,
}: {
  article: Article;
  onClose: () => void;
}) {
  const cleaned = cleanArticleContent(article.content ?? "");
  const hasContent = cleaned.trim().length > 50;

  function handleDownload() {
    const blob = new Blob([article.content], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${article.title}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="font-semibold text-gray-900 text-base leading-snug">{article.title}</h2>
            <p className="text-xs text-gray-400 mt-0.5">{article.keyword} · {article.wordCount} words</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-auto px-6 py-5">
          {hasContent ? (
            <div className="prose prose-sm max-w-none text-gray-800 prose-headings:font-bold prose-h1:text-2xl prose-h2:text-xl prose-h3:text-lg prose-p:leading-relaxed prose-strong:text-gray-900">
              <ArticleMarkdown content={cleaned} />
            </div>
          ) : (
            <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm">
              <span className="text-lg">⚠️</span>
              <span>Article content not found. Please check the data source.</span>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
          <button
            onClick={handleDownload}
            className="flex items-center gap-2 px-4 py-2 bg-gray-900 hover:bg-gray-800 text-white text-sm font-medium rounded-xl transition-colors"
          >
            <Download size={14} />
            Download .md
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function VersionHistoryModal({
  article,
  onClose,
}: {
  article: Article;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="font-semibold text-gray-900">Version History</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>
        <div className="p-6">
          {article.versions && article.versions.length > 0 ? (
            <div className="space-y-3">
              {article.versions.map((v) => (
                <div key={v.id} className="bg-gray-50 rounded-xl p-3 text-sm">
                  <div className="font-medium text-gray-700">{v.savedAt}</div>
                  {v.note && <div className="text-gray-500 text-xs mt-1">{v.note}</div>}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400 text-center py-6">No version history yet.</p>
          )}
          <div className="mt-4 pt-4 border-t border-gray-100">
            <p className="text-xs text-gray-500">Current version: {article.createdAt}</p>
          </div>
        </div>
        <div className="px-6 pb-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ContentLibraryPage() {
  const { articles } = useGlobalContext();
  const { config } = useBrand();
  const boardTags: BoardTag[] = config.boardTags && config.boardTags.length > 0
    ? config.boardTags
    : Object.entries(BOARD_CONFIG).map(([k, v]) => ({ key: k, ...v }));

  const [search, setSearch] = useState("");
  const [boardFilter, setBoardFilter] = useState("all");
  const [previewArticle, setPreviewArticle] = useState<Article | null>(null);
  const [historyArticle, setHistoryArticle] = useState<Article | null>(null);

  const filtered = articles.filter((a) => {
    const matchBoard = boardFilter === "all" || a.intentBoard === boardFilter;
    const matchSearch = !search || a.title.toLowerCase().includes(search.toLowerCase()) ||
      a.keyword.toLowerCase().includes(search.toLowerCase());
    return matchBoard && matchSearch;
  });

  function handleDownload(article: Article) {
    if (!article.content) {
      alert("No content to download.");
      return;
    }
    const blob = new Blob([article.content], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${article.title}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  return (
    <div className="min-h-screen bg-[#F5F5F7]">
      {/* Top Nav */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center gap-4">
        <Link to="/" className="text-gray-400 hover:text-gray-600 transition-colors">
          <ChevronLeft size={20} />
        </Link>
        <h1 className="text-lg font-semibold text-gray-900">Content Library</h1>
        <span className="text-sm text-gray-400 ml-1">{articles.length} articles</span>
      </div>

      <div className="p-6 max-w-7xl mx-auto">
        {/* Filters */}
        <div className="flex items-center gap-3 mb-5">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search articles..."
            className="w-64 bg-white border border-gray-200 rounded-xl px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
          <select
            value={boardFilter}
            onChange={(e) => setBoardFilter(e.target.value)}
            className="bg-white border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          >
            <option value="all">All Boards</option>
            {boardTags.map((t) => (
              <option key={t.key} value={t.key}>{t.label}</option>
            ))}
          </select>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Title</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Board</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Words</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Date</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center py-14 text-gray-400 text-sm">
                    No articles yet. Generate one in AI Creator.
                  </td>
                </tr>
              )}
              {filtered.map((article) => {
                const tag = findBoardTag(boardTags, article.intentBoard);
                return (
                  <tr key={article.id} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                    <td className="px-5 py-3.5">
                      <div className="font-medium text-gray-800 leading-snug">{article.title}</div>
                      <div className="text-xs text-gray-400 mt-0.5">{article.keyword}</div>
                    </td>
                    <td className="px-4 py-3.5">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${tag.bg} ${tag.text}`}>
                        {tag.label}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-gray-600">{article.wordCount.toLocaleString()}</td>
                    <td className="px-4 py-3.5">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        article.status === "published"
                          ? "bg-green-100 text-green-700"
                          : "bg-gray-100 text-gray-500"
                      }`}>
                        {article.status}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-gray-500 text-xs">{article.createdAt}</td>
                    <td className="px-4 py-3.5">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => setHistoryArticle(article)}
                          title="Version History"
                          className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                          <Clock size={15} />
                        </button>
                        <button
                          onClick={() => setPreviewArticle(article)}
                          title="Preview"
                          className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        >
                          <Eye size={15} />
                        </button>
                        <button
                          onClick={() => handleDownload(article)}
                          title="Download .md"
                          className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                        >
                          <Download size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {previewArticle && (
        <PreviewModal article={previewArticle} onClose={() => setPreviewArticle(null)} />
      )}
      {historyArticle && (
        <VersionHistoryModal article={historyArticle} onClose={() => setHistoryArticle(null)} />
      )}
    </div>
  );
}
